<form method="dialog">
  <button
    (click)="closeDialog.emit()"
    class="btn btn-circle btn-ghost btn-sm absolute right-2 top-2"
  >
    ✕
  </button>
</form>
<div>
  <h3 class="py-4 text-2xl font-semibold">Create Invoice</h3>
</div>

@if (fetchingProductVariantDurations || fetchingSalesReps) {
  <div class="my-4 text-center">
    <div class="loading loading-spinner loading-lg"></div>
  </div>
} @else if (errorFetchingProductVariantDurations || errorFetchingSalesReps) {
  <p class="my-4 text-center text-error">
    {{ errorFetchingProductVariantDurations || errorFetchingSalesReps }}
  </p>
} @else {
  <div class="flex flex-col space-y-4">
    <div class="flex">
      <div class="form-control w-fit px-4">
        <label class="label cursor-pointer">
          <input
            type="checkbox"
            class="checkbox"
            [(ngModel)]="promptDuration"
          />
          <span class="label-text ml-2"
            >Prompt customer to select durations?</span
          >
        </label>
      </div>
    </div>

    <div class="form-control px-4">
      <div class="flex space-x-4">
        <label class="label cursor-pointer">
          <input
            type="radio"
            name="priceExpirationMode"
            class="radio"
            [checked]="!useManualValues"
            (change)="useManualValues = false"
          />
          <span class="label-text ml-2"
            >Automatically calculate price and expiration</span
          >
        </label>
        <label class="label cursor-pointer">
          <input
            type="radio"
            name="priceExpirationMode"
            class="radio"
            [checked]="useManualValues"
            (change)="useManualValues = true"
          />
          <span class="label-text ml-2">Enter custom price and expiration</span>
        </label>
      </div>
    </div>

    <div class="flex">
      <div class="dropdown ml-4">
        <div tabindex="0" role="button" class="btn btn-outline btn-sm m-1">
          Select Sales Reps <app-chevron-down-icon class="h-4 w-4" />
        </div>
        <ul
          tabindex="0"
          class="menu dropdown-content z-[1] w-52 rounded-box bg-base-100 p-2 shadow"
        >
          @for (salesRep of availableSalesReps; track salesRep.id) {
            <li>
              <a (click)="addSalesRep(salesRep)">{{ salesRep.name }}</a>
            </li>
          }
        </ul>
      </div>
      @if (selectedSalesReps && selectedSalesReps.length > 0) {
        <div class="ml-4 flex items-center space-x-2">
          @for (salesRep of selectedSalesReps; track salesRep.id) {
            <div class="badge badge-outline">
              {{ salesRep.name }}
              <span
                class="ml-2 cursor-pointer"
                (click)="removeSalesRep(salesRep)"
                >X</span
              >
            </div>
          }
        </div>
      }
    </div>

    <div class="form-control w-full max-w-2xl px-4">
      <label class="label" for="invoice-note">
        <span class="label-text">Customer memo (optional)</span>
      </label>
      <textarea
        id="invoice-note"
        class="textarea textarea-bordered min-h-24 w-full"
        [(ngModel)]="invoiceNote"
        placeholder="Shown to the customer at checkout and on the receipt"
      ></textarea>
    </div>
  </div>

  <hr class="my-4 border-base-content/50" />

  <div class="flex justify-end space-x-4 pb-4">
    <div class="flex items-center text-sm">Filter Shown Licenses:</div>
    <div class="form-control">
      <label class="label cursor-pointer">
        <span class="label-text pr-2">Active</span>
        <input
          type="checkbox"
          class="checkbox checkbox-sm"
          [(ngModel)]="createInvoiceFilters.active"
          (change)="filterValidInvoiceLicenses()"
        />
      </label>
    </div>
    <div class="form-control">
      <label class="label cursor-pointer">
        <span class="label-text pr-2">Pending</span>
        <input
          type="checkbox"
          class="checkbox checkbox-sm"
          [(ngModel)]="createInvoiceFilters.pending"
          (change)="filterValidInvoiceLicenses()"
        />
      </label>
    </div>
    <div class="form-control">
      <label class="label cursor-pointer">
        <span class="label-text pr-2">Expired</span>
        <input
          type="checkbox"
          class="checkbox checkbox-sm"
          [(ngModel)]="createInvoiceFilters.expired"
          (change)="filterValidInvoiceLicenses()"
        />
      </label>
    </div>
  </div>

  <table class="table table-zebra">
    <thead class="text-base font-medium">
      <tr>
        <td>
          <input
            type="checkbox"
            class="checkbox"
            (change)="selectAllLicenses($event)"
          />
        </td>
        <td>Product</td>
        <td>Status</td>
        <td>Price</td>
        <td>Type</td>
        <td>Duration<br />(Months)</td>
        <td>Expiration</td>
        <td>Created</td>
      </tr>
    </thead>
    <tbody>
      @for (el of validInvoiceLicenses; track el.license) {
        <tr>
          <td>
            <input type="checkbox" class="checkbox" [(ngModel)]="el.checked" />
          </td>
          <td>{{ el.license.product.title }}</td>
          <td>{{ el.status }}</td>
          <td>
            @if (useManualValues && el.checked) {
              <input
                type="number"
                step="0.01"
                min="0"
                class="input input-sm input-bordered w-24"
                [(ngModel)]="el.manualPrice"
                [ngModelOptions]="{ standalone: true }"
              />
            } @else {
              {{ getLicensePrice(el.license) | currency: 'USD' }}
            }
          </td>
          <td>{{ getLicenseType(el.license) }}</td>
          <td>
            @if (fetchingProductVariantDurations) {
              <div class="loading loading-spinner loading-sm"></div>
            } @else {
              <select
                class="select select-bordered select-sm"
                [(ngModel)]="el.selectedDurationId"
                [disabled]="useManualValues"
              >
                @for (
                  option of getDurationOptions(el.license.product_variant.id);
                  track option.id
                ) {
                  <option [ngValue]="option.id">
                    {{ option.duration_months }}
                  </option>
                }
              </select>
            }
          </td>
          <td>
            @if (useManualValues && el.checked) {
              <input
                type="date"
                class="input input-sm input-bordered w-32"
                [(ngModel)]="el.manualExpiration"
                [ngModelOptions]="{ standalone: true }"
              />
            } @else {
              {{ el.license.expiration | date: 'MM/dd/yyyy' : 'UTC' }}
            }
          </td>
          <td>
            {{ el.license.created_at | date: 'MM/dd/yyyy, hh:mmaa' }}
          </td>
        </tr>
      } @empty {
        <tr>
          <td></td>
          <td colspan="4">No valid Licenses to add to invoice</td>
        </tr>
      }
    </tbody>
  </table>
}

<div class="mt-8 text-center">
  <button
    (click)="createInvoice()"
    class="btn btn-primary"
    [disabled]="creatingInvoice"
  >
    Create Invoice
  </button>
</div>

@if (errorCreatingInvoice) {
  <p class="mt-8 text-center text-error">
    {{ errorCreatingInvoice }}
  </p>
}
